/* eslint-disable no-console */
import {SearchOutlined, UploadOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Flex, Input, Popconfirm, Select, Table, TableProps} from 'antd';
import {ColumnType} from 'antd/es/table';
import moment from 'moment';
import React, {useEffect, useState, useMemo, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';

import DocumentUploadDrawerForm from '../../../components/DrawerForm/Utils/DocumentUploadDrawer';
import {
  frameworkDocuments,
  frameworkTypes
} from '../../../components/Forms/Constants/Documents/ESGConstants';
import ESGDocumentManagerForm from '../../../components/Forms/ESGDocumentManagerForm';
import {filterSelectOptions} from '../../../components/Forms/Utils/commonFunctions';
import Loader from '../../../components/Loader/Loader';
import ActionsButton from '../../../components/Table/Utils/ActionsButton';
import {APITypeEnum} from '../../../services/interface/tableAPIType';
import ESGDocumentAPI from '../../../services/modules/AIDocumentManager/ESGDocumentAPI';
import {uploadDocumentRecord} from '../../../services/modules/UploadAndJobs/uploadDocs';
import {hasUserRole, UserRole} from '../../../utils/commonFunctions';
import {yearSelect} from '../../../utils/FormUtils/yearSelect';
import mapAndFilter from '../../../utils/mapCountry';
import {StepState} from '../Constants/commonConstants';

interface DocumentType {
  _id: string;
  sno: string;
  docType: string;
  doc_type: string;
  file_display_name: string;
  uploadedAt: string;
  updatedAt: string;
}

const ESGDocument: React.FC = () => {
  const [showModel, setShowModel] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [refresh, setRefresh] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedYear, setSelectedYear] = useState<any>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const firstYear = yearSelect();
  const [dataSource, setDataSource] = useState<DocumentType[]>([]);
  const [frameworkType, setFrameworkType] = useState<any>('BRSR');
  const [documentType, setDocumentType] = useState<string | undefined>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const searchInput = useRef<InputRef>(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const SearchFilter = (
    title: string,
    dataIndex: keyof DocumentType
  ): ColumnType<DocumentType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value?.toString().toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const columns: TableProps<DocumentType>['columns'] = [
    {
      title: 'Sr No',
      dataIndex: '',
      render: (value: any, record: any, index: number) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      }
    },
    {
      title: 'File name',
      dataIndex: 'file_display_name',
      ...SearchFilter('File name', 'file_display_name')
    },
    {
      title: 'Document Type',
      dataIndex: 'doc_type',
      filters:
        documentType === 'all'
          ? mapAndFilter(dataSource, frameworkDocuments, 'doc_type').map(
              (item) => {
                return {text: item.label, value: item.value};
              }
            )
          : undefined,
      onFilter: (value, record) => {
        if (documentType !== 'all') {
          return true;
        }

        return record.doc_type === value;
      },
      render: (value) => {
        return (
          frameworkDocuments.find((item) => item.value === value)?.label ??
          value
        );
      }
    },
    {
      title: 'Uploaded at',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value) => moment(value).format('DD/MM/YYYY hh:mm A')
    },
    ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
      ? ([
          {
            title: 'Action',
            dataIndex: 'operation',
            type: '',
            fixed: 'right',
            width: '200px !important',
            render: (value: any, record: any) =>
              dataSource.length >= 1 ? (
                <ActionsButton
                  record={record}
                  setShowModel={setShowModel}
                  setEditData={setEditData}
                  setIsEdit={setIsEdit}
                  handleDelete={handleDelete}
                  noDelete={false}
                  isConfigurator={true}
                />
              ) : null
          }
        ] as any)
      : [])
  ];

  const filteredDocuments = useMemo(() => {
    if (!frameworkType) {
      return frameworkDocuments;
    }

    return frameworkDocuments.filter((doc) => doc.framework === frameworkType);
  }, [frameworkType]);

  useEffect(() => {
    setSelectedYear(searchParams.get('selectedYear') ?? firstYear?.[0].value);
    searchParams.set(
      'selectedYear',
      searchParams.get('selectedYear') ?? firstYear?.[0].value
    );

    const urlFrameworkType = searchParams.get('frameworkType');
    if (urlFrameworkType) {
      setFrameworkType(urlFrameworkType);
    }

    const urlDocType = searchParams.get('frameworkDocumentType');
    if (urlDocType) {
      const currentFrameworkType = urlFrameworkType ?? 'BRSR';

      const isValidDocType =
        urlDocType === 'all' ||
        frameworkDocuments.some(
          (doc) =>
            doc.framework === currentFrameworkType && doc.value === urlDocType
        );

      if (isValidDocType) {
        setDocumentType(urlDocType);
      } else {
        setDocumentType('all');
        searchParams.delete('frameworkDocumentType');
      }
    } else {
      setDocumentType('all');
    }

    setSearchParams(searchParams);
    setRefresh(true);
  }, []);

  useEffect(() => {
    if (frameworkType && documentType) {
      if (
        !frameworkDocuments.some(
          (doc) =>
            (doc.framework === frameworkType && doc.value === documentType) ||
            documentType === 'all'
        )
      ) {
        setDocumentType('all');
        handleSearch({name: 'frameworkDocumentType', value: 'all'});
      }
    }
  }, [frameworkType, documentType]);

  useEffect(() => {
    if (refresh) {
      if (selectedYear) {
        ESGDocumentAPI(selectedYear, frameworkType, documentType)
          .then((res) => {
            setPagination((prev) => ({
              ...prev,
              current: 1
            }));
            setDataSource(res);
            setRefresh(false);
          })
          .catch(() => {
            setRefresh(false);
          });
      } else {
        setRefresh(false);
      }
    }
  }, [refresh]);

  const handleSearch = (event: {name: string; value: string}) => {
    const {name, value} = event;
    if (value) {
      searchParams.set(name, value);
      setSearchParams(searchParams);
    } else {
      searchParams.delete(name);
      setSearchParams(searchParams);
    }
  };

  const handleDelete = (_id: string[] | string) => {
    Promise.all([
      uploadDocumentRecord(APITypeEnum.DELETE_ALL, {
        id: _id
      })
    ])
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
    }
  }, []);

  return (
    <>
      <Flex
        vertical
        style={{padding: 10, background: '#fff', borderRadius: 10}}
      >
        <Flex justify="space-between" align="center" style={{marginBottom: 20}}>
          <Flex gap={15}>
            <Select
              style={{width: '120px'}}
              placeholder="Year"
              value={selectedYear}
              options={firstYear}
              size="middle"
              popupMatchSelectWidth={false}
              onChange={(e) => {
                setSelectedYear(e);
                setRefresh(true);
                handleSearch({
                  name: 'selectedYear',
                  value: e
                });
              }}
            />

            <Select
              showSearch
              placeholder="Framework"
              value={frameworkType}
              style={{minWidth: '150px'}}
              defaultValue={''}
              disabled={true}
              options={frameworkTypes}
              popupMatchSelectWidth={false}
              size="middle"
              onChange={(e) => {
                setFrameworkType(e);
                handleSearch({
                  name: 'frameworkType',
                  value: e
                });
                if (documentType) {
                  const newFiltered = e
                    ? frameworkDocuments.filter((doc) => doc.framework === e)
                    : frameworkDocuments;
                  if (!newFiltered.some((doc) => doc.value === documentType)) {
                    setDocumentType(undefined);
                    handleSearch({name: 'frameworkDocumentType', value: ''});
                  }
                }
                setRefresh(true);
              }}
              filterOption={filterSelectOptions}
            />
            <Select
              showSearch
              placeholder="Document Type"
              value={documentType}
              style={{minWidth: '200px'}}
              defaultValue={'all'}
              options={[
                ...filteredDocuments,
                {
                  label: 'All Docs',
                  value: 'all'
                }
              ]}
              popupMatchSelectWidth={false}
              size="middle"
              onChange={(e) => {
                setDocumentType(e);
                setRefresh(true);
                handleSearch({
                  name: 'frameworkDocumentType',
                  value: e
                });
              }}
              filterOption={filterSelectOptions}
            />
          </Flex>
          <Flex gap={15}>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  handleDelete(selectedRowKeys);
                }}
              >
                <Button type="primary">
                  {selectedRowKeys.length >= 2 ? 'Delete Selected' : 'Delete'}
                </Button>
              </Popconfirm>
            )}
            <Button
              type="primary"
              icon={<UploadOutlined />}
              style={{background: '#3e5c3a'}}
              onClick={() => {
                setIsEdit(false);
                setShowModel(true);
              }}
            >
              Upload Document
            </Button>
          </Flex>
        </Flex>

        {/* Table */}
        <Table
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (
              selectedKey: React.Key[],
              selectedRow: DocumentType[]
            ) => {
              setSelectedRowKeys(
                selectedRow.map((item) => {
                  return item._id;
                })
              );
            }
          }}
          dataSource={dataSource}
          columns={columns}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize
          }}
          rowKey="_id"
          onChange={(page: any, _filters: any, _sorter: any, extra: any) => {
            if (extra?.action === 'filter') {
              setPagination((prev) => ({
                ...prev,
                current: 1,
                pageSize: page?.pageSize ?? prev.pageSize
              }));
            } else {
              setPagination({
                current: page.current,
                pageSize: page.pageSize
              });
            }
          }}
          loading={
            refresh ?? {
              indicator: <Loader />
            }
          }
        />
      </Flex>
      {showModel && (
        <DocumentUploadDrawerForm
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          API={uploadDocumentRecord}
          setRefresh={setRefresh}
          data={isEdit ? editData : undefined}
          hasOCRProcessing={false}
          FormComponent={<ESGDocumentManagerForm />}
          stepsTitle={['Details', 'Upload Document']}
          stepsType={[StepState.NORMAL, StepState.UPLOAD_AND_VALIDATE]}
        />
      )}
    </>
  );
};

export default ESGDocument;
